// Master Apps · Admin Inventário — tabela densa de todas as apps (porte fiel).
import { formatAppCode } from '@masterapps/shared';
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import { toast } from 'sonner';
import { AppPhoto, StatusBadge, VisibilityBadge } from '@/components/app-visuals';
import { IconChevronR, IconRefresh, IconSearch } from '@/components/icons';
import { FilterChip, Sidebar, TopBar } from '@/components/primitives';
import { SelectMenu, type SelectMenuTone } from '@/components/select-menu';
import { useDeleteApplication } from '@/hooks/use-applications';
import { costsByRepoSlug, repoSlug, useRailwayCosts } from '@/hooks/use-railway-costs';
import { byViewport, useViewport } from '@/hooks/use-viewport';
import { APP_TYPES, type App, STATUS_TYPES } from '@/lib/catalog-config';
import {
  COMPLIANCE_RED,
  complianceCheckState,
  complianceOf,
  DOC_KEYS,
  docsFilled,
} from '@/lib/compliance';
import { type CsvValue, csvFilename, downloadCsv } from '@/lib/csv';
import { formatDateBR, formatDateTimeBR, formatUsdBR } from '@/lib/format';
import {
  APRESENTACAO_OPCOES,
  APRESENTACAO_ROTULO,
  type ColunaTabela,
  CUSTO_VAZIO,
  casaFiltro,
  compararColuna,
  DIAS_OPCOES,
  diasComAcesso,
  diasUteisDoMes,
  type OrdemTabela,
  textoColuna,
  textoDias,
  valorColuna,
} from '@/lib/inventory-columns';
import type { ScreenProps } from '@/lib/theme';
import {
  fontMono,
  fontSans,
  makePalette,
  NOC_RED,
  numeroTabular,
  rotuloCaixaAlta,
} from '@/lib/theme';
import { type InventoryTableState, useInventoryPrefs } from '@/store/inventory-prefs';

// Os tipos das colunas moram em `@/lib/inventory-columns`; a store de
// preferências e quem mais precisar deles importa de lá.
export type { ColunaTabela, OrdemTabela } from '@/lib/inventory-columns';

const DANGER = '#dc2626';

// Colunas da tabela · a última guarda as ações por linha (editar/excluir).
//
// UMA <table> de verdade com `tableLayout: fixed` e <colgroup>: cabeçalho,
// linha de filtros e cada linha de dados compartilham as colunas POR
// CONSTRUÇÃO, seja qual for o conteúdo. A versão anterior eram três grids CSS
// separados com o mesmo template — e em produção (thumbs, "PRJ-TI-MASTERAPPS",
// "US$ 2,19", nomes compridos) cada grid resolvia as frações a seu modo e a
// linha de filtros saía enfileirada à esquerda, sem casar com as colunas.
//
// Larguras FIXAS em px (o valor é a coluna inteira, com o respiro de
// `CELL_PADDING_X` de cada lado já dentro): seleção 36 (12 de recuo externo +
// caixa nativa de 13 + folga); Visibilidade 96 (rótulo "VISIBILIDADE ↕" a
// 9.5px = 84); Situação 112 (o selo "HOMOLOGAÇÃO" mede 108 — recorta 1-2px no
// notebook e o `title` da célula carrega o rótulo); Apresentação 104 (rótulo
// 98); Docs 76 e Dias úteis 76 (o select de filtro precisa mostrar "Todos" +
// seta INTEIROS — a 56 o Docs cortava em "T…"); Atualizada 84 ("17/09/2026" a 11px = 62); Custo/mês 88
// ("US$ 1.234,56" a 11px ≈ 78); Ações 128 = Editar 45 + Excluir 49 + vãos +
// chevron 12 (110 medidos) + 6 de respiro à esquerda + 12 do recuo externo à
// direita — o conteúdo é alinhado à direita e termina no recuo da tabela, sem
// sobra de coluna.
//
// As duas ELÁSTICAS dividem o que sobrar: Aplicação fica com 3/4 e Responsável
// com 1/4 (o `calc` do Responsável usa a largura da tabela; Aplicação é a única
// coluna `auto`, então recebe o resto). Nas larguras de notebook os dois
// truncam com reticências — e o `title` de cada célula leva o valor inteiro.
/** Respiro horizontal DENTRO de cada célula (o vão visual entre colunas é 2×). */
const CELL_PADDING_X = 6;
/** Recuo da primeira e da última coluna em relação à moldura da tabela. */
const TABLE_PADDING_X = 12;

type ColunaChave = ColunaTabela | 'selecao' | 'acoes';

interface ColunaDef {
  key: ColunaChave;
  /** Largura em px; ausente = elástica (ver `RESPONSAVEL_WIDTH`). */
  width?: number;
}

const COLUNAS: ReadonlyArray<ColunaDef> = [
  { key: 'selecao', width: 36 },
  { key: 'aplicacao' },
  { key: 'visibilidade', width: 96 },
  { key: 'situacao', width: 128 },
  { key: 'responsavel' },
  { key: 'apresentacao', width: 96 },
  { key: 'docs', width: 76 },
  { key: 'dias', width: 76 },
  { key: 'atualizada', width: 84 },
  { key: 'custo', width: 88 },
  { key: 'acoes', width: 128 },
];

/**
 * Responsável tem largura FIXA e Aplicação (a única `auto`) fica com todo o
 * resto. Era `calc((100% - fixas) / 3)` numa <col>: o Chromium ignora `calc`
 * com percentual em <col> de tabela `fixed` e dividia a sobra meio a meio —
 * no notebook o nome da aplicação cabia em 11 caracteres enquanto o
 * responsável aparecia inteiro. 132px mostram "MATHEUS VILELA M…" e o `title`
 * da célula leva o nome completo.
 */
const RESPONSAVEL_WIDTH = 132;

/**
 * Piso de largura da tabela quando ela rola de lado (tablet). Abaixo dele as
 * elásticas somem e o nome da aplicação desaparece atrás do avatar de 32px.
 *
 * O piso tem de caber no notebook: a 1280px de janela a linha tem ~1010px
 * (220 da barra lateral + 24 de recuo de cada lado), então 1000 deixa o desktop
 * inteiro sem barra e só o tablet (≤1024) rola dentro do painel.
 */
const TABLE_MIN_WIDTH = 1000;

/**
 * Rolagem lateral DELIBERADA da tabela fora do mobile — não é estouro, é a
 * saída honesta: comprimir onze colunas apaga conteúdo, e virar card jogaria
 * fora a leitura em tabela que a auditoria usa para comparar aplicações linha
 * a linha. `scrollbarWidth: thin` para a barra não roubar altura das linhas.
 */
const TABLE_SCROLLER: CSSProperties = { overflowX: 'auto', scrollbarWidth: 'thin' };

/** Alinhamento do conteúdo de uma coluna. */
type Alinhamento = 'left' | 'right';

/** Colunas numéricas (e Ações) alinham à direita — cabeçalho, filtro e célula. */
const ALINHAMENTO: Partial<Record<ColunaChave, Alinhamento>> = {
  docs: 'right',
  dias: 'right',
  custo: 'right',
  acoes: 'right',
};

/**
 * Estilo-base de QUALQUER célula (<th> ou <td>): recorta o que não cabe (é a
 * coluna que manda, nunca o conteúdo), respiro padrão e recuo externo nas
 * pontas. `verticalAlign: middle` porque a linha tem duas alturas de texto
 * (nome + código) e os selos precisam centrar nela.
 */
function celula(key: ColunaChave): CSSProperties {
  const primeira = key === COLUNAS[0]?.key;
  const ultima = key === COLUNAS[COLUNAS.length - 1]?.key;
  return {
    paddingLeft: primeira ? TABLE_PADDING_X : CELL_PADDING_X,
    paddingRight: ultima ? TABLE_PADDING_X : CELL_PADDING_X,
    textAlign: ALINHAMENTO[key] ?? 'left',
    verticalAlign: 'middle',
    overflow: 'hidden',
    boxSizing: 'border-box',
  };
}

/** Célula de TEXTO: uma linha, reticências — o `title` fica por conta de quem chama. */
const TEXTO_UMA_LINHA: CSSProperties = {
  whiteSpace: 'nowrap',
  textOverflow: 'ellipsis',
};

/**
 * ABRIR A APLICAÇÃO · camada de clique que cobre a linha (ou o card) inteira.
 * Compartilhada pelos dois formatos de propósito: é o arranjo já validado no
 * navegador — <button> nativo sobreposto em vez de `role="button"` no contêiner,
 * porque a linha contém botões de verdade (selecionar, editar, excluir) e botão
 * dentro de botão é HTML inválido. Sendo o primeiro filho no DOM, o Tab pega
 * "Abrir <app>" antes dos controles.
 */
const OPEN_LAYER: CSSProperties = {
  position: 'absolute',
  inset: 0,
  zIndex: 1,
  padding: 0,
  background: 'transparent',
  border: 'none',
  cursor: 'pointer',
};

/**
 * Contraparte do OPEN_LAYER: sobe os blocos interativos ACIMA da camada de
 * clique. Sem isto a sobreposição comeria o clique do checkbox e das ações.
 */
const CONTROL_LAYER: CSSProperties = { position: 'relative', zIndex: 2 };

const VISIBILITY_LABELS: Record<string, string> = {
  publico: 'Público',
  privado: 'Privado',
  interno: 'Interno',
};

const CRITICALITY_LABELS: Record<string, string> = {
  alta: 'Crítica',
  media: 'Média',
  baixa: 'Baixa',
};

// Colunas do CSV de auditoria, na ordem em que saem no arquivo. Rótulos são os
// mesmos que a tela mostra (nada de enum cru na planilha) e os cinco links de
// documentação vão inteiros, para o auditor conseguir abrir cada um.
const CSV_COLUMNS: ReadonlyArray<{ header: string; value: (a: App) => CsvValue }> = [
  { header: 'ID', value: (a) => a.id },
  { header: 'Nome', value: (a) => a.name },
  { header: 'Slug', value: (a) => a.slug },
  { header: 'Tipo', value: (a) => APP_TYPES[a.type]?.label ?? a.type },
  { header: 'Visibilidade', value: (a) => VISIBILITY_LABELS[a.visibility] ?? a.visibility },
  { header: 'Situação', value: (a) => STATUS_TYPES[a.status]?.label ?? a.status },
  { header: 'Criticidade', value: (a) => CRITICALITY_LABELS[a.criticality] ?? a.criticality },
  { header: 'Responsável', value: (a) => a.owner },
  { header: 'Time', value: (a) => a.team },
  { header: 'Projeto', value: (a) => a.project },
  { header: 'Chamado', value: (a) => a.ticket },
  { header: 'LGPD', value: (a) => a.lgpd },
  { header: 'Dados sensíveis', value: (a) => a.dadosSensiveis },
  { header: 'Integração externa', value: (a) => a.integracaoExterna },
  { header: 'Uso de IA', value: (a) => a.usoIa },
  { header: 'Levantamento de requisitos', value: (a) => a.requirements },
  { header: 'Documentação técnica', value: (a) => a.docsTechnical },
  { header: 'Regra de negócio', value: (a) => a.docsBusiness },
  { header: 'Documentação de API', value: (a) => a.docsApi },
  { header: 'Repositório', value: (a) => a.repo },
  { header: 'Atualizada em', value: (a) => formatDateBR(a.lastUpdate) },
  { header: 'Publicada em', value: (a) => formatDateBR(a.deployedAt) },
];
const CSV_HEADERS = CSV_COLUMNS.map((c) => c.header);

const PAGE_SIZES = [25, 50, 100];

// Coluna Documentação · fonte única de verdade: a regra oficial de
// conformidade de `@/lib/compliance`, a mesma do acervo de documentos, da ficha
// da aplicação e da tela de auditoria. Só a fração de artefatos COM LINK sobre
// o total de DOC_KEYS — os cinco quadradinhos coloridos que existiam aqui
// custavam 140px de coluna e saíram para as colunas Apresentação e Dias no mês
// caberem no notebook sem rolagem. O detalhe (dispensados/pendentes) fica no
// `title`; pendência pinta o número de vermelho, sem chip.
function DocumentationCell({ app, dark }: Readonly<{ app: App; dark: boolean }>) {
  const p = makePalette(dark);
  const checks = complianceOf(app).checks.filter((c) => c.kind === 'doc');
  const filled = docsFilled(app);
  const exempt = checks.filter((c) => complianceCheckState(c) === 'dispensado').length;
  // Pendência = critério documental não atendido. Dispensado conta como
  // atendido (regra oficial), logo nunca entra nesta conta.
  const pending = checks.filter((c) => !c.ok).length;
  return (
    <div
      title={[
        `${filled} de ${DOC_KEYS.length} artefatos com link`,
        `${exempt} ${exempt === 1 ? 'dispensado' : 'dispensados'}`,
        `${pending} ${pending === 1 ? 'pendente' : 'pendentes'}`,
      ].join(' · ')}
      style={{
        ...numeroTabular,
        fontSize: 11.5,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        color: pending > 0 ? COMPLIANCE_RED : p.text,
      }}
    >
      {filled}/{DOC_KEYS.length}
    </div>
  );
}

/**
 * Coluna APRESENTAÇÃO · a aplicação entra no deck da apresentação? É o inverso
 * de `apresentacaoDesabilitada` (o check "Desabilitar apresentação" do
 * cadastro e da lista do lobby). Ponto discreto + palavra, sem selo colorido:
 * a coluna é de conferência, não de alerta.
 */
function PresentationCell({
  app,
  dark,
  accent,
}: Readonly<{ app: App; dark: boolean; accent: string }>) {
  const p = makePalette(dark);
  const visivel = !app.apresentacaoDesabilitada;
  return (
    <div
      title={
        visivel
          ? 'Visível na apresentação'
          : 'Oculta na apresentação (desabilitada no cadastro ou no lobby)'
      }
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 6,
        minWidth: 0,
        fontSize: 11.5,
        color: visivel ? p.text : p.muted,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      <span
        aria-hidden
        style={{
          width: 7,
          height: 7,
          borderRadius: '50%',
          flexShrink: 0,
          background: visivel ? accent : 'transparent',
          border: `1px solid ${visivel ? accent : p.border}`,
        }}
      />
      {visivel ? APRESENTACAO_ROTULO.visivel : APRESENTACAO_ROTULO.oculto}
    </div>
  );
}

/**
 * Coluna DIAS ÚTEIS · "02/22": dias ÚTEIS (seg–sex) do mês da coleta com pelo
 * menos um acesso, coletados do Clerk de cada aplicação (`acessosDiasMes`),
 * sobre os dias úteis daquele mês — a mesma fração que o slide da apresentação
 * mostra. "—" quando não há contagem ou não há carimbo de coleta; zero sai
 * "00/22" (o Clerk respondeu zero de fato). Sans com figuras tabulares
 * (`numeroTabular`) — a mono é só para identificador.
 */
function DaysCell({ app, dark }: Readonly<{ app: App; dark: boolean }>) {
  const p = makePalette(dark);
  const dias = diasComAcesso(app);
  const texto = textoDias(dias, app.acessosColetadoEm);
  let title = 'Sem coleta de acessos neste mês';
  if (dias !== null && app.acessosColetadoEm) {
    const uteis = diasUteisDoMes(app.acessosColetadoEm);
    title = `${dias} ${dias === 1 ? 'dia útil' : 'dias úteis'} com acesso de ${uteis} dias úteis no mês da coleta`;
  }
  if (app.acessosErro) title = `${title} · última coleta falhou: ${app.acessosErro}`;
  return (
    <div
      title={title}
      style={{
        ...numeroTabular,
        fontSize: 11.5,
        textAlign: 'right',
        whiteSpace: 'nowrap',
        color: dias === null ? p.muted : p.text,
      }}
    >
      {texto}
    </div>
  );
}

/**
 * Coluna CUSTO · estimativa mensal do projeto no Railway em dólar.
 *
 * O tooltip carrega o que não cabe na célula: que é ESTIMATIVA (uso do mês ×
 * tabela de preços, não fatura) e o motivo do "—" quando não há número.
 * `numeroTabular` (sans da marca com figuras tabulares) para os valores
 * alinharem pela vírgula na coluna.
 */
function CostCell({ custoUsd, dark }: Readonly<{ custoUsd: number | null; dark: boolean }>) {
  const p = makePalette(dark);
  const desconhecido = custoUsd === null;
  return (
    <div
      title={
        desconhecido
          ? 'Sem custo estimado: a aplicação não tem repositório cadastrado, o repositório não corresponde a nenhum projeto do Railway ou os custos não foram carregados.'
          : `Estimativa de custo do mês no Railway · ${formatUsdBR(custoUsd)} (memória, CPU e egress)`
      }
      style={{
        ...numeroTabular,
        // 11 e não 11.5: "US$ 1.234,56" precisa caber nos 88px da coluna.
        fontSize: 11,
        textAlign: 'right',
        color: desconhecido ? p.muted : p.text,
        whiteSpace: 'nowrap',
        overflow: 'hidden',
        textOverflow: 'ellipsis',
      }}
    >
      {desconhecido ? CUSTO_VAZIO : formatUsdBR(custoUsd)}
    </div>
  );
}

// Mesmo desenho do ButtonGhost de primitives, com os dois estados que a tela
// precisa e o primitivo não expõe: desabilitado (paginação nas pontas) e ativo
// (página atual).
function GhostButton({
  children,
  dark,
  onClick,
  disabled = false,
  active = false,
  numerico = false,
  title,
}: Readonly<{
  children: React.ReactNode;
  dark: boolean;
  onClick?: () => void;
  disabled?: boolean;
  active?: boolean;
  numerico?: boolean;
  title?: string;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // No toque o botão cresce para 40px — é o piso de alvo que a sonda cobra, e a
  // paginação e o Exportar CSV são justamente o que se erra com o dedo. No
  // desktop continua 32px, o valor original.
  const side = byViewport(vp, { mobile: 40, desktop: 32 });
  return (
    <button
      type="button"
      title={title}
      disabled={disabled}
      onClick={onClick}
      style={{
        minWidth: side,
        height: side,
        padding: '0 12px',
        background: active ? p.text : 'transparent',
        border: `1px solid ${active ? p.text : p.border}`,
        borderRadius: 6,
        fontSize: 12,
        fontWeight: active ? 600 : 500,
        color: active ? p.surface : p.textMuted,
        ...(numerico ? numeroTabular : { fontFamily: fontSans }),
        opacity: disabled ? 0.4 : 1,
        cursor: disabled ? 'default' : 'pointer',
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 6,
      }}
    >
      {children}
    </button>
  );
}

// Checkbox de seleção — o estado indeterminado só existe via propriedade do
// DOM, então precisa do ref.
function SelectCheckbox({
  checked,
  indeterminate = false,
  onToggle,
  accent,
  label,
  touch = false,
  id,
}: Readonly<{
  checked: boolean;
  indeterminate?: boolean;
  onToggle: () => void;
  accent: string;
  label: string;
  /** Lista de cards do celular: a caixa nativa de 13px não se acerta com o dedo. */
  touch?: boolean;
  /** Só quando existe um <label htmlFor> visível ao lado (faixa do celular). */
  id?: string;
}>) {
  const ref = useRef<HTMLInputElement | null>(null);
  useEffect(() => {
    if (ref.current) ref.current.indeterminate = indeterminate && !checked;
  }, [indeterminate, checked]);
  const box = (
    <input
      ref={ref}
      id={id}
      type="checkbox"
      checked={checked}
      title={label}
      aria-label={label}
      onChange={onToggle}
      onClick={(e: React.MouseEvent<HTMLInputElement>) => e.stopPropagation()}
      style={{
        accentColor: accent,
        cursor: 'pointer',
        margin: 0,
        // Sem `touch` (grade do tablet e do desktop) não há dimensão nenhuma no
        // estilo: a caixa fica com o tamanho nativo de 13×13, exatamente como
        // antes — é essa medida que o vão de 32px da primeira coluna abraça.
        width: touch ? 20 : undefined,
        height: touch ? 20 : undefined,
      }}
    />
  );
  // Grade do tablet e do desktop: o input sai NU, sem embrulho nenhum, então o
  // DOM continua idêntico ao de antes.
  if (!touch) return box;
  // Celular · a sonda mede a caixa nativa em 20×20, metade do piso de 40px. Não
  // dá para resolver com `padding`: o Chrome IGNORA padding em
  // `input[type=checkbox]` — injetei um no navegador com `padding: 10px` (nas
  // duas caixas, `border-box` e `content-box`) e o retângulo continuou 20×20 nos
  // dois casos. E inflar o `width/height` para 40 desenharia uma caixa do
  // tamanho da foto da aplicação ao lado. Então o alvo real é o <label> em
  // volta: 40×40, aciona o checkbox pelo comportamento nativo do rótulo (nada de
  // handler novo) e mantém a caixinha desenhada em 20px.
  return (
    <label
      style={{
        display: 'inline-flex',
        alignItems: 'center',
        justifyContent: 'center',
        width: 40,
        height: 40,
        flexShrink: 0,
        cursor: 'pointer',
      }}
    >
      {box}
    </label>
  );
}

// Ação por linha (Editar / Excluir). `stopPropagation` no clique é obrigatório:
// a linha inteira navega para o detalhe da aplicação.
function RowAction({
  children,
  dark,
  onClick,
  danger = false,
  disabled = false,
  title,
  touch = false,
}: Readonly<{
  children: React.ReactNode;
  dark: boolean;
  onClick: () => void;
  danger?: boolean;
  disabled?: boolean;
  title: string;
  /**
   * Card do celular: 40px de altura e metade da largura do card cada. Na tabela
   * fica em 26px — a coluna de ações tem 128px para os dois botões e o chevron,
   * e crescer a altura esticaria a linha inteira.
   */
  touch?: boolean;
}>) {
  const p = makePalette(dark);
  // Desabilitado ganha do perigo: um botão fora de uso não se anuncia em
  // vermelho como se a ação estivesse disponível.
  const enabledColor = danger ? DANGER : p.textMuted;
  const color = disabled ? p.muted : enabledColor;
  return (
    <button
      type="button"
      title={title}
      aria-label={title}
      disabled={disabled}
      onClick={(e: React.MouseEvent<HTMLButtonElement>) => {
        e.stopPropagation();
        onClick();
      }}
      style={{
        height: touch ? 40 : 26,
        flex: touch ? 1 : undefined,
        padding: touch ? '0 14px' : '0 5px',
        background: 'transparent',
        border: `1px solid ${p.border}`,
        borderRadius: touch ? 8 : 5,
        fontSize: touch ? 12.5 : 10.5,
        fontWeight: 700,
        fontFamily: fontSans,
        color,
        opacity: disabled ? 0.5 : 1,
        cursor: disabled ? 'progress' : 'pointer',
        whiteSpace: 'nowrap',
      }}
    >
      {children}
    </button>
  );
}

/**
 * Item da paginação: uma página clicável (`page`) ou o furo entre duas faixas
 * (`page: null`). O `key` é estável — identifica a página, ou as duas páginas
 * que o furo separa — para a lista não depender do índice do array.
 */
interface PageItem {
  key: string;
  page: number | null;
}

const pageItem = (n: number): PageItem => ({ key: `page-${n}`, page: n });

// Janela de páginas: 1 · … · 4 5 6 · … · 20
function pageWindow(current: number, total: number): PageItem[] {
  if (total <= 7) return Array.from({ length: total }, (_, i) => pageItem(i + 1));
  const wanted = [1, total, current - 1, current, current + 1].filter((n) => n >= 1 && n <= total);
  const unique = [...new Set(wanted)].sort((a, b) => a - b);
  const out: PageItem[] = [];
  let prev = 0;
  for (const n of unique) {
    if (prev > 0 && n - prev > 1) out.push({ key: `gap-${prev}-${n}`, page: null });
    out.push(pageItem(n));
    prev = n;
  }
  return out;
}

/**
 * Rodapé da tabela: "Mostrando 1 a 25 de 87 aplicações · filtradas de 120" — ou
 * o aviso de filtro vazio. Fora do JSX porque a frase tem plural e um trecho
 * condicional.
 */
function rangeSummary(first: number, last: number, shown: number, total: number): string {
  if (shown === 0) return `Nenhuma aplicação no filtro atual · ${total} no total`;
  const noun = shown === 1 ? 'aplicação' : 'aplicações';
  const scope = shown === total ? '' : ` · filtradas de ${total}`;
  return `Mostrando ${first} a ${last} de ${shown} ${noun}${scope}`;
}

/**
 * Uma aplicação da página, com tudo o que os DOIS formatos precisam — a linha da
 * grade (tablet/desktop) e o card (celular). Interface nomeada de propósito: os
 * dois componentes têm a mesma assinatura, então quem monta a tabela escolhe o
 * formato sem mexer no conjunto de dados que passa.
 */
interface RowProps {
  app: App;
  dark: boolean;
  accent: string;
  /**
   * Custo estimado no Railway (USD/mês) desta aplicação, ou `null` quando ela não
   * tem repositório, o repositório não casou com nenhum projeto ou a rota de
   * custos ainda não respondeu. `null` vira "—" — nunca "US$ 0,00".
   */
  custoUsd: number | null;
  selected: boolean;
  /** A última linha da página não desenha a divisória (só a grade usa). */
  divider: boolean;
  /** Exclusão desta linha em voo: desabilita as ações. */
  removing: boolean;
  onOpen: () => void;
  onToggleSelect: () => void;
  onEdit: () => void;
  onDelete: () => void;
}

// Uma linha da tabela. A linha inteira abre o detalhe da aplicação — é um
// controle de verdade, então também responde a Enter/Espaço. O checkbox de
// seleção e os botões de ação param a propagação do clique (cada um cuida do
// seu), por isso o teclado só age quando o foco está na própria linha.
function InventoryGridRow({
  app,
  dark,
  accent,
  custoUsd,
  selected,
  divider,
  removing,
  onOpen,
  onToggleSelect,
  onEdit,
  onDelete,
}: Readonly<RowProps>) {
  const p = makePalette(dark);
  const background = selected ? p.surface2 : 'transparent';
  const td = (key: ColunaChave, extra?: CSSProperties): CSSProperties => ({
    ...celula(key),
    paddingTop: 10,
    paddingBottom: 10,
    borderBottom: divider ? `1px solid ${p.border}` : 'none',
    ...extra,
  });
  return (
    // A linha abre a aplicação, mas o <tr> NÃO leva `role="button"`: ela contém
    // o checkbox de seleção e os botões Editar/Excluir, e botão dentro de botão
    // é HTML inválido. O clique da linha é do <button> sobreposto (OPEN_LAYER),
    // cujo bloco contentor é o <tr> em `position: relative` — cobre a linha
    // inteira, de ponta a ponta, sem ocupar célula.
    <tr
      style={{
        fontSize: 12.5,
        cursor: 'pointer',
        transition: 'background 120ms',
        background,
        position: 'relative',
      }}
      onMouseEnter={(e: React.MouseEvent<HTMLTableRowElement>) => {
        e.currentTarget.style.background = p.surface2;
      }}
      onMouseLeave={(e: React.MouseEvent<HTMLTableRowElement>) => {
        e.currentTarget.style.background = background;
      }}
    >
      <td style={td('selecao')}>
        {/*
          ABRIR A APLICAÇÃO — <button> nativo cobrindo a linha (OPEN_LAYER), no
          lugar de `role="button"` + `tabIndex` + `onKeyDown` no <tr>: foco,
          Enter e Espaço saem do elemento, sem precisar comparar `e.target` com
          `e.currentTarget` para não roubar a tecla do checkbox e das ações.
          Vem primeiro no DOM, então o Tab pega "Abrir <app>" antes de
          selecionar/editar.
        */}
        <button
          type="button"
          onClick={onOpen}
          aria-label={`Abrir ${app.name}`}
          style={OPEN_LAYER}
        />
        {/* `zIndex` acima da camada de clique nos dois blocos interativos da
            linha (aqui e nas ações): sem isso a sobreposição comeria o clique. */}
        <div style={{ ...CONTROL_LAYER, display: 'flex' }}>
          <SelectCheckbox
            checked={selected}
            onToggle={onToggleSelect}
            accent={accent}
            label={`Selecionar ${app.name}`}
          />
        </div>
      </td>
      <td
        style={td('aplicacao')}
        title={`${app.name} · ${formatAppCode(app.code)} · ${app.project ?? ''}`}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minWidth: 0 }}>
          <AppPhoto app={app} size={32} dark={dark} />
          <div style={{ minWidth: 0, flex: 1 }}>
            <div
              style={{
                ...TEXTO_UMA_LINHA,
                fontSize: 13,
                fontWeight: 600,
                color: p.text,
                overflow: 'hidden',
              }}
            >
              {app.name}
            </div>
            <div
              style={{
                ...TEXTO_UMA_LINHA,
                fontSize: 10,
                color: p.muted,
                fontFamily: fontMono,
                overflow: 'hidden',
              }}
            >
              {formatAppCode(app.code)} · {app.project}
            </div>
          </div>
        </div>
      </td>
      {/* Selos em célula `overflow: hidden`: um chip mais largo que a coluna é
          recortado em vez de empurrar as vizinhas; o `title` leva o rótulo. */}
      <td style={td('visibilidade')} title={VISIBILITY_LABELS[app.visibility] ?? app.visibility}>
        <div style={{ display: 'flex' }}>
          <VisibilityBadge visibility={app.visibility} dark={dark} />
        </div>
      </td>
      <td style={td('situacao')} title={STATUS_TYPES[app.status]?.label ?? app.status}>
        <div style={{ display: 'flex' }}>
          <StatusBadge status={app.status} dark={dark} />
        </div>
      </td>
      <td
        style={td('responsavel', { ...TEXTO_UMA_LINHA, fontSize: 12, color: p.text })}
        title={app.owner}
      >
        {app.owner}
      </td>
      <td style={td('apresentacao')}>
        <PresentationCell app={app} dark={dark} accent={accent} />
      </td>
      <td style={td('docs')}>
        <DocumentationCell app={app} dark={dark} />
      </td>
      <td style={td('dias')}>
        <DaysCell app={app} dark={dark} />
      </td>
      <td
        style={td('atualizada', {
          ...numeroTabular,
          ...TEXTO_UMA_LINHA,
          fontSize: 11,
          color: p.textMuted,
        })}
        title={formatDateBR(app.lastUpdate)}
      >
        {formatDateBR(app.lastUpdate)}
      </td>
      <td style={td('custo')}>
        <CostCell custoUsd={custoUsd} dark={dark} />
      </td>
      <td style={td('acoes')}>
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            gap: 2,
            color: p.muted,
            ...CONTROL_LAYER,
          }}
        >
          <RowAction dark={dark} onClick={onEdit} title={`Editar ${app.name}`} disabled={removing}>
            Editar
          </RowAction>
          <RowAction
            dark={dark}
            danger
            onClick={onDelete}
            title={`Excluir ${app.name}`}
            disabled={removing}
          >
            {removing ? 'Excluindo…' : 'Excluir'}
          </RowAction>
          {/* Chevron encostado no Excluir: a coluna é medida para os dois botões. */}
          <IconChevronR size={12} style={{ marginLeft: -1, flexShrink: 0 }} />
        </div>
      </td>
    </tr>
  );
}

/**
 * Um atributo dentro do card: RÓTULO à esquerda, valor à direita. O rótulo antes
 * do valor é o que substitui o cabeçalho de coluna — sem ele, "11/08/2026" e
 * "PAULO ARRUDA" soltos num card não dizem de que campo são.
 */
function CardField({
  label,
  children,
  dark,
}: Readonly<{ label: string; children: ReactNode; dark: boolean }>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        gap: 10,
        minWidth: 0,
      }}
    >
      <div
        style={{
          ...rotuloCaixaAlta,
          fontSize: 10,
          fontWeight: 700,
          letterSpacing: '0.08em',
          textTransform: 'uppercase',
          color: p.muted,
          flexShrink: 0,
        }}
      >
        {label}
      </div>
      {/* `overflowWrap: anywhere` e não reticências: no card há largura para
          embrulhar, e cortar o nome do responsável é perder o dado. */}
      <div style={{ minWidth: 0, textAlign: 'right', overflowWrap: 'anywhere', color: p.text }}>
        {children}
      </div>
    </div>
  );
}

/**
 * A mesma aplicação em 375px de tela. A grade de nove colunas não tem como
 * caber: 554px dela não encolhem (colunas fixas + vãos), então o card é o único
 * formato usável — cada atributo numa linha própria, com rótulo antes do valor.
 *
 * O arranjo de clique é o MESMO da linha da grade, e por isso importa repetir:
 * camada `OPEN_LAYER` abrindo a aplicação por baixo de tudo, checkbox e bloco de
 * ações em `CONTROL_LAYER` (zIndex 2) para o toque neles não cair na camada de
 * abrir. O chevron fica FORA do bloco de ações, para o canto dele também abrir.
 */
function InventoryCard({
  app,
  dark,
  accent,
  custoUsd,
  selected,
  removing,
  onOpen,
  onToggleSelect,
  onEdit,
  onDelete,
}: Readonly<RowProps>) {
  const p = makePalette(dark);
  return (
    <div
      style={{
        // Containing block da camada de abrir — o mesmo papel que `position:
        // relative` tem na linha da grade.
        position: 'relative',
        background: selected ? p.surface2 : p.surface,
        border: `1px solid ${selected ? accent : p.border}`,
        borderRadius: 10,
        padding: 12,
        display: 'flex',
        flexDirection: 'column',
        gap: 10,
        fontSize: 12.5,
      }}
    >
      <button type="button" onClick={onOpen} aria-label={`Abrir ${app.name}`} style={OPEN_LAYER} />

      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 10 }}>
        {/* 40 e não os 22 de antes: o rótulo de toque de 40×40 que embrulha o
            checkbox (ver SelectCheckbox) transbordaria uma caixa de 22px. Este
            card só existe no celular, então não há faixa a proteger aqui. */}
        <div style={{ ...CONTROL_LAYER, display: 'flex', alignItems: 'center', height: 40 }}>
          <SelectCheckbox
            touch
            checked={selected}
            onToggle={onToggleSelect}
            accent={accent}
            label={`Selecionar ${app.name}`}
          />
        </div>
        <AppPhoto app={app} size={40} dark={dark} />
        <div style={{ flex: 1, minWidth: 0 }}>
          <div
            style={{
              fontSize: 14,
              fontWeight: 600,
              color: p.text,
              overflowWrap: 'anywhere',
            }}
          >
            {app.name}
          </div>
          <div
            style={{
              fontSize: 10,
              color: p.muted,
              fontFamily: fontMono,
              overflowWrap: 'anywhere',
            }}
          >
            {formatAppCode(app.code)} · {app.project}
          </div>
        </div>
        <span style={{ color: p.muted, display: 'flex', flexShrink: 0, marginTop: 2 }}>
          <IconChevronR size={16} />
        </span>
      </div>

      {/* Visibilidade e situação são etiquetas curtas e auto-explicativas:
          embrulham numa faixa própria, sem rótulo, para não gastar linhas. */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap' }}>
        <VisibilityBadge visibility={app.visibility} dark={dark} />
        <StatusBadge status={app.status} dark={dark} />
      </div>

      <CardField label="Responsável" dark={dark}>
        {app.owner}
      </CardField>
      <CardField label="Apresentação" dark={dark}>
        <PresentationCell app={app} dark={dark} accent={accent} />
      </CardField>
      <CardField label="Documentação" dark={dark}>
        <DocumentationCell app={app} dark={dark} />
      </CardField>
      <CardField label="Dias úteis" dark={dark}>
        <DaysCell app={app} dark={dark} />
      </CardField>
      <CardField label="Atualizada" dark={dark}>
        <span style={{ ...numeroTabular, fontSize: 11.5 }}>{formatDateBR(app.lastUpdate)}</span>
      </CardField>
      <CardField label="Custo/mês" dark={dark}>
        <CostCell custoUsd={custoUsd} dark={dark} />
      </CardField>

      <div style={{ ...CONTROL_LAYER, display: 'flex', gap: 8 }}>
        <RowAction
          touch
          dark={dark}
          onClick={onEdit}
          title={`Editar ${app.name}`}
          disabled={removing}
        >
          Editar
        </RowAction>
        <RowAction
          touch
          dark={dark}
          danger
          onClick={onDelete}
          title={`Excluir ${app.name}`}
          disabled={removing}
        >
          {removing ? 'Excluindo…' : 'Excluir'}
        </RowAction>
      </div>
    </div>
  );
}

// Id do checkbox de "selecionar a página" no celular: lá ele não mora num
// cabeçalho de coluna, e sem um <label> visível ao lado ninguém adivinha o que a
// caixa solta faz. O `htmlFor` também dá ao rótulo o alvo de toque de 40px.
const SELECT_PAGE_ID = 'inventario-selecionar-pagina';

/**
 * A tabela — e é aqui que a responsividade mora, não na tela, porque a escolha
 * do formato depende só da faixa de viewport e do conjunto de linhas:
 *
 *  - desktop (>1024px): a <table> dentro do painel arredondado, sem rolagem;
 *  - tablet (641–1024px): a MESMA <table>, dentro de um rolador lateral com
 *    piso de largura (`TABLE_MIN_WIDTH`). Rolar de lado é deliberado: é o preço
 *    de manter a leitura em tabela, que é como a auditoria compara aplicações;
 *  - celular (≤640px): pilha de cards, e o "selecionar a página" — que na grade
 *    era o checkbox do cabeçalho — ganha uma faixa própria com rótulo.
 */
/** Rótulos de cada coluna nos controles de filtro (aria/placeholder). */
const ROTULO_COLUNA: Record<ColunaTabela, string> = {
  aplicacao: 'Aplicação',
  visibilidade: 'Visibilidade',
  situacao: 'Situação',
  responsavel: 'Responsável',
  apresentacao: 'Apresentação',
  docs: 'Docs',
  dias: 'Dias úteis',
  atualizada: 'Atualizada',
  custo: 'Custo/mês',
};

/**
 * Cabeçalho ORDENÁVEL: botão nativo (teclado de graça) que alterna
 * asc → desc → ordem de cadastro. A seta cheia só aparece na coluna ativa; nas
 * demais fica o indicador neutro esmaecido, para a affordance existir sem virar
 * ruído.
 */
function SortHeader({
  rotulo,
  col,
  ordem,
  onOrdenar,
  accent,
  end,
  title,
}: Readonly<{
  rotulo: string;
  col: ColunaTabela;
  ordem: OrdemTabela;
  onOrdenar: (col: ColunaTabela) => void;
  accent: string;
  /** Alinha o rótulo à DIREITA — para coluna numérica (docs, dias, custo). */
  end?: boolean;
  /** Tooltip do cabeçalho; por padrão "Ordenar por <rótulo>". */
  title?: string;
}>) {
  const ativo = ordem?.col === col;
  let seta = '↕';
  if (ativo) seta = ordem?.dir === 1 ? '▲' : '▼';
  return (
    <button
      type="button"
      onClick={() => onOrdenar(col)}
      title={title ?? `Ordenar por ${rotulo}`}
      style={{
        background: 'none',
        border: 'none',
        padding: 0,
        cursor: 'pointer',
        display: 'flex',
        justifyContent: end ? 'flex-end' : 'flex-start',
        gap: 4,
        // Ocupa a célula inteira e NUNCA a alarga: a <table> é `fixed`, a
        // coluna manda. Rótulo comprido ("Apresentação", "Dias úteis") quebra
        // em DUAS linhas em vez de virar reticências — cabeçalho cortado
        // esconde o nome da coluna.
        width: '100%',
        minWidth: 0,
        maxWidth: '100%',
        whiteSpace: 'normal',
        textAlign: end ? 'right' : 'left',
        alignItems: 'flex-end',
        // Herda o visual do cabeçalho (fonte, caixa alta, espaçamento).
        font: 'inherit',
        color: ativo ? accent : 'inherit',
        textTransform: 'inherit',
        letterSpacing: 'inherit',
        fontWeight: 'inherit',
      }}
    >
      <span style={{ minWidth: 0 }}>{rotulo}</span>
      <span style={{ opacity: ativo ? 1 : 0.35, fontSize: 9, flexShrink: 0 }}>{seta}</span>
    </button>
  );
}

/** Estilo comum dos controles da linha de filtro — compacto, mono, tema. */
function filtroEstilo(dark: boolean): CSSProperties {
  const p = makePalette(dark);
  return {
    width: '100%',
    minWidth: 0,
    height: 26,
    padding: '0 8px',
    background: p.surface,
    color: p.text,
    border: `1px solid ${p.border}`,
    borderRadius: 6,
    fontSize: 11,
    fontFamily: fontSans,
    outline: 'none',
    boxSizing: 'border-box',
  };
}

function FiltroTexto({
  col,
  filtroCol,
  onFiltrar,
  dark,
  placeholder,
}: Readonly<{
  col: ColunaTabela;
  filtroCol: Partial<Record<ColunaTabela, string>>;
  onFiltrar: (col: ColunaTabela, v: string) => void;
  dark: boolean;
  placeholder?: string;
}>) {
  return (
    <input
      value={filtroCol[col] ?? ''}
      onChange={(e: React.ChangeEvent<HTMLInputElement>) => onFiltrar(col, e.target.value)}
      placeholder={placeholder ?? 'Filtrar…'}
      aria-label={`Filtrar por ${ROTULO_COLUNA[col]}`}
      style={filtroEstilo(dark)}
    />
  );
}

/**
 * Tone do SelectMenu com a paleta do tema — a mesma moldura dos inputs do
 * produto (borda 1px da paleta, fundo `surface`, hover/seleção no acento).
 */
function selectTone(dark: boolean, accent: string): SelectMenuTone {
  const p = makePalette(dark);
  return {
    bg: p.surface,
    text: p.text,
    border: p.border,
    borderActive: `${accent}66`,
    panelBg: p.surface,
    panelBorder: p.border,
    optionHover: p.surface2,
    optionSelected: accent,
    muted: p.muted,
  };
}

function FiltroSelect({
  col,
  valores,
  opcoes,
  filtroCol,
  onFiltrar,
  dark,
  accent,
}: Readonly<{
  col: ColunaTabela;
  /** Valores EXIBIDOS distintos — vira "Todos" + um item por valor. */
  valores?: string[];
  /** Opções FIXAS (já com "Todos") — Apresentação e Dias no mês usam estas. */
  opcoes?: ReadonlyArray<{ value: string; label: string }>;
  filtroCol: Partial<Record<ColunaTabela, string>>;
  onFiltrar: (col: ColunaTabela, v: string) => void;
  dark: boolean;
  accent: string;
}>) {
  return (
    // SelectMenu no lugar do <select>: a lista do nativo é desenhada pelo
    // sistema e destoava do resto da tabela. O `''` continua sendo o "Todos".
    <SelectMenu
      value={filtroCol[col] ?? ''}
      onChange={(v) => onFiltrar(col, v)}
      label={`Filtrar por ${ROTULO_COLUNA[col]}`}
      options={
        opcoes
          ? [...opcoes]
          : [{ value: '', label: 'Todos' }, ...(valores ?? []).map((v) => ({ value: v, label: v }))]
      }
      tone={{
        ...selectTone(dark, accent),
        // Medidas do filtroEstilo: compacto, mono, fonte 11.
        height: 26,
        fontSize: 11,
        radius: 6,
        fontFamily: fontSans,
        fontWeight: 500,
        width: '100%',
      }}
    />
  );
}

function InventoryTable({
  rows,
  dark,
  accent,
  empty,
  selected,
  removingId,
  allPageSelected,
  selectedOnPage,
  onTogglePage,
  onOpen,
  onToggleSelect,
  onEdit,
  onDelete,
  ordem,
  onOrdenar,
  filtroCol,
  onFiltrarColuna,
  onLimparFiltros,
  opcoes,
  custoDeApp,
}: Readonly<{
  rows: App[];
  dark: boolean;
  accent: string;
  /** Nenhuma aplicação passou pelos filtros: o aviso vai abaixo do cabeçalho. */
  empty: boolean;
  selected: ReadonlySet<string>;
  removingId: string | null;
  allPageSelected: boolean;
  selectedOnPage: number;
  onTogglePage: () => void;
  onOpen: (app: App) => void;
  onToggleSelect: (app: App) => void;
  onEdit: (app: App) => void;
  onDelete: (app: App) => void;
  ordem: OrdemTabela;
  onOrdenar: (col: ColunaTabela) => void;
  filtroCol: Partial<Record<ColunaTabela, string>>;
  onFiltrarColuna: (col: ColunaTabela, v: string) => void;
  onLimparFiltros: () => void;
  /** Valores distintos (já exibíveis) para os selects enumeráveis. */
  opcoes: Record<'visibilidade' | 'situacao' | 'responsavel' | 'docs', string[]>;
  /** Custo estimado (USD/mês) da aplicação, ou null quando não há correspondência. */
  custoDeApp: (a: App) => number | null;
}>) {
  const p = makePalette(dark);
  const vp = useViewport();
  // Um verbo só, usado no rótulo acessível e no texto visível — as duas frases
  // não podem divergir do estado da caixa.
  const pageVerb = allPageSelected ? 'Desmarcar' : 'Selecionar';
  // Mesma assinatura (`RowProps`) nos dois formatos: escolher é trocar o
  // componente, não remontar a lista de props.
  const Row = vp.mobile ? InventoryCard : InventoryGridRow;

  const list = rows.map((a, i) => (
    <Row
      key={a.id}
      app={a}
      dark={dark}
      accent={accent}
      custoUsd={custoDeApp(a)}
      selected={selected.has(a.id)}
      divider={i < rows.length - 1}
      removing={removingId === a.id}
      onOpen={() => onOpen(a)}
      onToggleSelect={() => onToggleSelect(a)}
      onEdit={() => onEdit(a)}
      onDelete={() => onDelete(a)}
    />
  ));

  const emptyNote = empty && (
    <div style={{ padding: 32, textAlign: 'center', color: p.muted, fontSize: 13 }}>
      Nenhuma aplicação corresponde aos filtros.
    </div>
  );

  // Cabeçalho: célula <th> com o visual de rótulo em caixa alta. 9.5/0.06em
  // (eram 10/0.08em): o rótulo é o que dita a largura mínima de Visibilidade,
  // Apresentação e Atualizada — com onze colunas no notebook de 1280px cada
  // pixel do rótulo sai do nome da aplicação.
  const th = (key: ColunaChave): CSSProperties => ({
    ...celula(key),
    ...rotuloCaixaAlta,
    paddingTop: 10,
    paddingBottom: 10,
    borderBottom: `1px solid ${p.border}`,
    background: p.surface2,
    fontSize: 9,
    fontWeight: 700,
    color: p.muted,
    textTransform: 'uppercase',
    letterSpacing: '0.01em',
    // Rótulo pode quebrar em DUAS linhas ("Dias úteis", "Apresentação"):
    // cortar o cabeçalho com reticências escondia o nome da coluna.
    whiteSpace: 'normal',
    lineHeight: 1.15,
    verticalAlign: 'bottom',
  });
  // Linha de FILTROS: também <th> (é cabeçalho da coluna, só que interativo),
  // sem o visual de rótulo — o controle dentro dela ocupa 100% da célula.
  const thFiltro = (key: ColunaChave): CSSProperties => ({
    ...celula(key),
    paddingTop: 8,
    paddingBottom: 8,
    borderBottom: `1px solid ${p.border}`,
    background: p.surface2,
    fontWeight: 400,
  });

  const grid = (
    <table
      style={{
        // `fixed` + <colgroup>: a largura de cada coluna vem das <col>, não do
        // conteúdo — é o que amarra cabeçalho, filtros e células. `width: 100%`
        // preenche o painel; o piso (`minWidth`) só age no tablet, onde o
        // rolador (TABLE_SCROLLER) assume.
        tableLayout: 'fixed',
        width: '100%',
        minWidth: TABLE_MIN_WIDTH,
        borderCollapse: 'collapse',
        borderSpacing: 0,
      }}
    >
      <colgroup>
        {COLUNAS.map((c) => (
          <col
            key={c.key}
            style={{
              width: c.width ?? (c.key === 'responsavel' ? RESPONSAVEL_WIDTH : undefined),
            }}
          />
        ))}
      </colgroup>
      <thead>
        <tr>
          <th style={th('selecao')} scope="col">
            <div style={{ display: 'flex' }}>
              <SelectCheckbox
                checked={allPageSelected}
                indeterminate={selectedOnPage > 0}
                onToggle={onTogglePage}
                accent={accent}
                label={`${pageVerb} as aplicações desta página`}
              />
            </div>
          </th>
          <th style={th('aplicacao')} scope="col">
            <SortHeader
              rotulo="Aplicação"
              col="aplicacao"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          <th style={th('visibilidade')} scope="col">
            <SortHeader
              rotulo="Visibilidade"
              col="visibilidade"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          <th style={th('situacao')} scope="col">
            <SortHeader
              rotulo="Situação"
              col="situacao"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          <th style={th('responsavel')} scope="col">
            <SortHeader
              rotulo="Responsável"
              col="responsavel"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          <th style={th('apresentacao')} scope="col">
            <SortHeader
              rotulo="Apresentação"
              col="apresentacao"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          {/* "Docs" e não "Documentação": a coluna é só a fração "5/5" e o
              rótulo comprido é que ditava a largura antiga. */}
          <th style={th('docs')} scope="col">
            <SortHeader
              rotulo="Docs"
              col="docs"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
              end
            />
          </th>
          {/* "Dias úteis" numa linha só; o que a fração significa vai no title. */}
          <th style={th('dias')} scope="col">
            <SortHeader
              rotulo="Dias úteis"
              col="dias"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
              end
              title="Dias úteis do mês com acesso / dias úteis do mês"
            />
          </th>
          <th style={th('atualizada')} scope="col">
            <SortHeader
              rotulo="Atualizada"
              col="atualizada"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
            />
          </th>
          {/* Custo · ordena pelo NÚMERO (ver valorColuna). */}
          <th style={th('custo')} scope="col">
            <SortHeader
              rotulo="Custo/mês"
              col="custo"
              ordem={ordem}
              onOrdenar={onOrdenar}
              accent={accent}
              end
            />
          </th>
          <th style={th('acoes')} scope="col">
            Ações
          </th>
        </tr>
        {/* Linha de FILTROS por coluna — texto onde o valor é livre, select
            onde é enumerável. Ações não filtra; a célula dela vira o "Limpar"
            quando há filtro ativo. */}
        <tr>
          <th style={thFiltro('selecao')} aria-label="Sem filtro" />
          <th style={thFiltro('aplicacao')}>
            <FiltroTexto
              col="aplicacao"
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
            />
          </th>
          <th style={thFiltro('visibilidade')}>
            <FiltroSelect
              col="visibilidade"
              valores={opcoes.visibilidade}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          <th style={thFiltro('situacao')}>
            <FiltroSelect
              col="situacao"
              valores={opcoes.situacao}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          <th style={thFiltro('responsavel')}>
            <FiltroSelect
              col="responsavel"
              valores={opcoes.responsavel}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          <th style={thFiltro('apresentacao')}>
            <FiltroSelect
              col="apresentacao"
              opcoes={APRESENTACAO_OPCOES}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          <th style={thFiltro('docs')}>
            <FiltroSelect
              col="docs"
              valores={opcoes.docs}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          <th style={thFiltro('dias')}>
            <FiltroSelect
              col="dias"
              opcoes={DIAS_OPCOES}
              filtroCol={filtroCol}
              onFiltrar={onFiltrarColuna}
              dark={dark}
              accent={accent}
            />
          </th>
          {/* Atualizada ordena mas não filtra (decisão de produto). O custo
              também não: um select com um valor distinto por aplicação (todos
              os "US$ x,yz") não é filtro, é a própria coluna repetida — quem
              quer achar o mais caro ordena pela seta, que é numérica de verdade. */}
          <th style={thFiltro('atualizada')} aria-label="Sem filtro" />
          <th style={thFiltro('custo')} aria-label="Sem filtro" />
          <th style={thFiltro('acoes')}>
            {Object.values(filtroCol).some(Boolean) && (
              <button
                type="button"
                onClick={onLimparFiltros}
                style={{
                  height: 26,
                  padding: '0 10px',
                  background: 'transparent',
                  border: `1px solid ${p.border}`,
                  color: p.textMuted,
                  borderRadius: 6,
                  fontSize: 11,
                  fontWeight: 600,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                }}
              >
                Limpar
              </button>
            )}
          </th>
        </tr>
      </thead>
      <tbody>
        {empty && (
          <tr>
            <td colSpan={COLUNAS.length} style={{ padding: 0 }}>
              {emptyNote}
            </td>
          </tr>
        )}
        {list}
      </tbody>
    </table>
  );

  // No celular o painel deixa de ser moldura (cada card tem a sua) e passa a ser
  // só a pilha. No tablet e no desktop é a mesma moldura arredondada de antes.
  const frame: CSSProperties = {
    background: p.surface,
    border: `1px solid ${p.border}`,
    borderRadius: 10,
    overflow: 'hidden',
  };
  const panel = byViewport<CSSProperties>(vp, {
    mobile: { display: 'flex', flexDirection: 'column', gap: 10 },
    tablet: frame,
    desktop: frame,
  });

  return (
    <div style={panel}>
      {vp.mobile && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, minHeight: 40 }}>
          <SelectCheckbox
            touch
            id={SELECT_PAGE_ID}
            checked={allPageSelected}
            indeterminate={selectedOnPage > 0}
            onToggle={onTogglePage}
            accent={accent}
            label={`${pageVerb} as aplicações desta página`}
          />
          <label
            htmlFor={SELECT_PAGE_ID}
            style={{
              flex: 1,
              alignSelf: 'stretch',
              display: 'flex',
              alignItems: 'center',
              fontSize: 11,
              fontFamily: fontSans,
              color: p.muted,
              cursor: 'pointer',
            }}
          >
            {pageVerb} esta página
          </label>
        </div>
      )}
      {vp.mobile && emptyNote}
      {vp.mobile && list}
      {!vp.mobile && <div style={TABLE_SCROLLER}>{grid}</div>}
    </div>
  );
}

export function ScreenAdminInventory({
  dark,
  accent = NOC_RED,
  apps,
  active = 'inventory',
  initialFilter = 'all',
  title = 'Inventário',
}: Readonly<
  ScreenProps & {
    apps: App[];
    active?: string;
    initialFilter?: string;
    title?: string;
  }
>) {
  const p = makePalette(dark);
  const vp = useViewport();
  const navigate = useNavigate();
  // Recorte da tabela (chips, busca, página, ordenação, filtros de coluna) num
  // store por tela: abrir a ficha de um app e voltar NÃO zera o que o usuário
  // montou. `active` separa Inventário de Apps internos. A seleção fica de
  // fora: exportar é ação do momento, não preferência.
  const saved = useInventoryPrefs((s) => s.byKey[active]);
  const save = useInventoryPrefs((s) => s.save);
  const estado: InventoryTableState = saved ?? {
    filter: initialFilter,
    query: '',
    page: 1,
    pageSize: PAGE_SIZES[0],
    ordem: null,
    filtroCol: {},
  };
  const { filter, query, page, pageSize, ordem, filtroCol } = estado;
  const patch = (parcial: Partial<InventoryTableState>) => save(active, { ...estado, ...parcial });
  // Seleção por id — vale para exportar só o que foi marcado.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  const searchRef = useRef<HTMLInputElement | null>(null);
  // Ações de admin por linha. A rota desta tela já é admin-only.
  const remove = useDeleteApplication();
  const removingId = remove.isPending ? remove.variables : null;

  /**
   * Custo do Railway. A tela inteira é admin-only (rota `AdminOnly`), então a
   * consulta pode rodar sem checar papel aqui — e é a mesma queryKey do catálogo
   * e da tela de gastos, com `staleTime` de 1 hora: navegar entre elas não gera
   * request novo.
   *
   * Erro NÃO é tratado: a coluna simplesmente mostra "—". Enquanto a rota não
   * existir (404), o inventário continua funcionando igual.
   */
  const { data: costs } = useRailwayCosts();
  const custoPorSlug = costsByRepoSlug(costs?.custos);
  // A amarração é pelo REPOSITÓRIO (último segmento, sem diferenciar caixa):
  // aplicação sem repositório ou sem projeto correspondente devolve null.
  const custoDeApp = (a: App): number | null => {
    const slug = repoSlug(a.repo);
    if (!slug) return null;
    return custoPorSlug.get(slug)?.estimadoMesUsd ?? null;
  };

  // O atalho anunciado no campo precisa existir de verdade.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.ctrlKey || e.metaKey) && e.key.toLowerCase() === 'k') {
        e.preventDefault();
        searchRef.current?.focus();
        searchRef.current?.select();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);

  // Mudar o recorte volta para a página 1 (filtrar na página 3 e cair numa
  // lista vazia é o bug clássico) e limpa a seleção — linha que saiu da tela
  // não pode continuar contando para a exportação.
  const changeFilter = (value: string) => {
    patch({ filter: value, page: 1 });
    setSelected(new Set());
  };
  const changeQuery = (value: string) => {
    patch({ query: value, page: 1 });
    setSelected(new Set());
  };
  // Trocar o tamanho da página não muda o recorte: a seleção continua valendo.
  const changePageSize = (value: number) => {
    patch({ pageSize: value, page: 1 });
  };

  const totals = {
    all: apps.length,
    publico: apps.filter((a) => a.visibility === 'publico').length,
    privado: apps.filter((a) => a.visibility === 'privado').length,
    interno: apps.filter((a) => a.visibility === 'interno').length,
    prod: apps.filter((a) => a.status === 'prod').length,
    homolog: apps.filter((a) => a.status === 'homolog').length,
    manutencao: apps.filter((a) => a.status === 'manutencao').length,
    // Pausado ganha chip próprio (dev e depreciado não têm) porque é o único
    // recorte do inventário que existe para VIRAR decisão: é a fila do que
    // pode ser retomado. Sem o chip, um projeto congelado só reaparece se
    // alguém lembrar de buscar por ele.
    pausado: apps.filter((a) => a.status === 'pausado').length,
    critica: apps.filter((a) => a.criticality === 'alta').length,
    lgpd: apps.filter((a) => a.lgpd).length,
  };

  const q = query.trim().toLowerCase();
  const matchesFilter = (a: App) => {
    switch (filter) {
      case 'publico':
      case 'privado':
      case 'interno':
        return a.visibility === filter;
      case 'prod':
      case 'homolog':
      case 'manutencao':
      case 'pausado':
        return a.status === filter;
      case 'critica':
        return a.criticality === 'alta';
      case 'lgpd':
        return a.lgpd;
      default:
        return true;
    }
  };
  const matchesQuery = (a: App) =>
    !q ||
    [a.name, a.owner, a.team, a.project ?? '', a.id, a.slug].some((f) =>
      String(f).toLowerCase().includes(q),
    );
  // Base = chips + busca; os filtros de coluna entram DEPOIS (e as opções dos
  // selects saem da base, para um filtro não esvaziar as opções dos outros).
  const baseChips = apps.filter((a) => matchesFilter(a) && matchesQuery(a));
  const matchesColunas = (a: App) =>
    (Object.entries(filtroCol) as [ColunaTabela, string][]).every(([col, v]) =>
      casaFiltro(a, col, v),
    );
  const filtered = baseChips.filter(matchesColunas);

  // Ordenação estável: cópia + compararColuna (número como número, texto em
  // pt-BR, null sempre por último).
  const ordered = ordem
    ? [...filtered].sort((x, y) =>
        compararColuna(
          valorColuna(x, ordem.col, custoDeApp(x)),
          valorColuna(y, ordem.col, custoDeApp(y)),
          ordem.dir,
        ),
      )
    : filtered;

  const ordenarPor = (col: ColunaTabela) => {
    // asc → desc → volta à ordem de cadastro no terceiro clique.
    const proxima: OrdemTabela =
      ordem?.col !== col ? { col, dir: 1 } : ordem.dir === 1 ? { col, dir: -1 } : null;
    patch({ ordem: proxima, page: 1 });
  };
  const filtrarColuna = (col: ColunaTabela, v: string) => {
    patch({ filtroCol: { ...filtroCol, [col]: v }, page: 1 });
  };
  const limparFiltrosColuna = () => {
    patch({ filtroCol: {}, page: 1 });
  };
  // Opções dos selects (valores EXIBIDOS distintos, no escopo dos chips/busca).
  const opcoesColuna = (col: ColunaTabela) =>
    [...new Set(baseChips.map((a) => textoColuna(a, col)))].sort((a, b) =>
      a.localeCompare(b, 'pt-BR', { numeric: true }),
    );

  // ── Paginação (cliente) ───────────────────────────────────────────────────
  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  // Clamp de segurança: a lista pode encolher entre renders (refetch).
  const currentPage = Math.min(page, totalPages);
  const start = (currentPage - 1) * pageSize;
  const pageRows = ordered.slice(start, start + pageSize);
  const firstShown = filtered.length === 0 ? 0 : start + 1;
  const lastShown = start + pageRows.length;

  // ── Seleção ───────────────────────────────────────────────────────────────
  const pageIds = pageRows.map((a) => a.id);
  const selectedOnPage = pageIds.filter((id) => selected.has(id)).length;
  const allPageSelected = pageRows.length > 0 && selectedOnPage === pageRows.length;
  const togglePage = () => {
    setSelected((prev) => {
      const next = new Set(prev);
      for (const id of pageIds) {
        if (allPageSelected) next.delete(id);
        else next.add(id);
      }
      return next;
    });
  };
  const toggleRow = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  // ── Ações por linha ───────────────────────────────────────────────────────
  const editApp = (app: App) => navigate(`/admin/applications/${app.slug}/edit`);

  // Confirmação NOMEANDO a aplicação: a linha clicada e a linha excluída não
  // podem ser confundidas numa tabela densa. A exclusão leva as associações de
  // acesso com ela (FK em cascata), então o texto avisa.
  const deleteApp = (app: App) => {
    if (remove.isPending) return;
    const ok = window.confirm(
      `Excluir a aplicação "${app.name}"?\n\nEla sai do catálogo e do inventário, junto das restrições de acesso cadastradas. Esta ação não pode ser desfeita.`,
    );
    if (!ok) return;
    remove.mutate(app.id, {
      onSuccess: () => {
        // A linha deixou de existir: não pode continuar contando na exportação.
        setSelected((prev) => {
          if (!prev.has(app.id)) return prev;
          const next = new Set(prev);
          next.delete(app.id);
          return next;
        });
        toast.success(`"${app.name}" foi excluída do inventário`);
      },
      onError: (e: Error) => toast.error(e.message),
    });
  };

  // ── Exportação ────────────────────────────────────────────────────────────
  // Exporta o que está na tela (lista FILTRADA, não a base) — ou apenas as
  // linhas marcadas, quando houver seleção.
  const selectedRows = filtered.filter((a) => selected.has(a.id));
  const exportRows = selectedRows.length > 0 ? selectedRows : filtered;
  const selectedNoun =
    selectedRows.length === 1 ? 'aplicação selecionada' : 'aplicações selecionadas';
  const exportTitle =
    selectedRows.length > 0
      ? `Exportar ${selectedRows.length} ${selectedNoun} em CSV`
      : `Exportar as ${filtered.length} aplicações do filtro atual em CSV`;
  const exportApps = () => {
    if (exportRows.length === 0) return;
    const prefix = active === 'internal' ? 'master-apps-internos' : 'master-apps-inventario';
    downloadCsv(
      csvFilename(prefix),
      exportRows.map((a) =>
        Object.fromEntries(CSV_COLUMNS.map((c) => [c.header, c.value(a)] as [string, CsvValue])),
      ),
      CSV_HEADERS,
    );
  };

  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        fontFamily: fontSans,
        color: p.text,
        background: p.bg,
        display: 'flex',
        overflow: 'hidden',
      }}
    >
      <Sidebar active={active} dark={dark} accent={accent} role="admin" />

      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0 }}>
        <TopBar
          dark={dark}
          title={title}
          subtitle={`${totals.all} apps · ${totals.publico} públicos · ${totals.privado} privados · ${totals.interno} internos`}
          right={
            <>
              <GhostButton
                dark={dark}
                onClick={exportApps}
                disabled={exportRows.length === 0}
                title={exportTitle}
              >
                <IconRefresh size={13} /> Exportar CSV
                {selectedRows.length > 0 ? ` (${selectedRows.length})` : ''}
              </GhostButton>
              <button
                type="button"
                onClick={() => navigate('/admin/applications/new')}
                style={{
                  // 34px medidos pela sonda a 375px (137×34): é a ação principal
                  // da tela e sobe para o piso de 40 no celular. No desktop segue
                  // com os 34 de sempre, ao lado do Exportar CSV.
                  height: byViewport(vp, { mobile: 40, tablet: 34, desktop: 34 }),
                  padding: '0 14px',
                  background: accent,
                  color: '#fff',
                  border: 'none',
                  borderRadius: 7,
                  fontSize: 12,
                  fontWeight: 600,
                  fontFamily: fontSans,
                  cursor: 'pointer',
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 6,
                }}
              >
                <span style={{ fontSize: 16, lineHeight: 0.7 }}>+</span> Nova aplicação
              </button>
            </>
          }
        />

        <div
          style={{
            flex: 1,
            // 24px de folga de cada lado em 320px de tela são 15% da largura
            // útil — largura que a tabela precisa mais do que a margem.
            padding: byViewport(vp, { mobile: 12, tablet: 16, desktop: 24 }),
            overflow: 'auto',
            minHeight: 0,
          }}
        >
          {/* Chips de filtro rápido · contam sempre sobre a base, não sobre a página */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              gap: 8,
              flexWrap: 'wrap',
              marginBottom: 16,
            }}
          >
            <FilterChip
              label="Todos"
              count={totals.all}
              active={filter === 'all'}
              onClick={() => changeFilter('all')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Públicos"
              count={totals.publico}
              active={filter === 'publico'}
              onClick={() => changeFilter('publico')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Privados"
              count={totals.privado}
              active={filter === 'privado'}
              onClick={() => changeFilter('privado')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Internos"
              count={totals.interno}
              active={filter === 'interno'}
              onClick={() => changeFilter('interno')}
              dark={dark}
              accent={accent}
            />
            <div style={{ width: 1, height: 24, background: p.border, margin: '0 4px' }} />
            <FilterChip
              label="Produção"
              count={totals.prod}
              active={filter === 'prod'}
              onClick={() => changeFilter('prod')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Homologação"
              count={totals.homolog}
              active={filter === 'homolog'}
              onClick={() => changeFilter('homolog')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Manutenção"
              count={totals.manutencao}
              active={filter === 'manutencao'}
              onClick={() => changeFilter('manutencao')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="Pausado"
              count={totals.pausado}
              active={filter === 'pausado'}
              onClick={() => changeFilter('pausado')}
              dark={dark}
              accent={accent}
            />
            <div style={{ width: 1, height: 24, background: p.border, margin: '0 4px' }} />
            <FilterChip
              label="Crítica"
              count={totals.critica}
              active={filter === 'critica'}
              onClick={() => changeFilter('critica')}
              dark={dark}
              accent={accent}
            />
            <FilterChip
              label="LGPD"
              count={totals.lgpd}
              active={filter === 'lgpd'}
              onClick={() => changeFilter('lgpd')}
              dark={dark}
              accent={accent}
            />

            <div style={{ flex: 1 }} />

            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 8,
                // No toque a caixa cresce para 40px de alvo; no celular ela ainda
                // ocupa a linha inteira, porque 220px fixos ao lado dos chips
                // deixavam a busca encavalada na última fileira de filtros.
                //
                // 44 e não 40: com `box-sizing: border-box` (globals.css) a
                // moldura de 40 menos os 2px de borda deixa 38 para o campo, e a
                // sonda mediu exatamente `input 256×38` — abaixo do piso. Com 44
                // na moldura o campo alcança os 40px sem mudar de largura.
                height: byViewport(vp, { mobile: 44, desktop: 30 }),
                padding: '0 10px',
                background: p.surface,
                border: `1px solid ${p.border}`,
                borderRadius: 6,
                width: byViewport<number | string>(vp, {
                  mobile: '100%',
                  tablet: 220,
                  desktop: 220,
                }),
                fontSize: 11,
                color: p.muted,
              }}
            >
              <IconSearch size={13} />
              <input
                ref={searchRef}
                value={query}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => changeQuery(e.target.value)}
                placeholder="Buscar aplicação…"
                style={{
                  flex: 1,
                  background: 'transparent',
                  border: 'none',
                  outline: 'none',
                  fontSize: 11,
                  color: p.text,
                  fontFamily: fontSans,
                  // `minHeight` e não `height`: no desktop 0 é o valor padrão, a
                  // caixa de texto continua com os 17px de antes. No celular são
                  // os 40px do piso (a moldura acima ganhou 44 para caber).
                  minHeight: byViewport(vp, { mobile: 40, desktop: 0 }),
                }}
              />
              <span
                style={{
                  fontSize: 9,
                  fontFamily: fontMono,
                  padding: '2px 5px',
                  borderRadius: 3,
                  border: `1px solid ${p.border}`,
                }}
              >
                Ctrl+K
              </span>
            </div>
          </div>

          {/* Tabela · grade no desktop, grade com rolagem lateral no tablet,
              lista de cards no celular (a escolha vive em InventoryTable). */}
          <InventoryTable
            rows={pageRows}
            dark={dark}
            accent={accent}
            empty={filtered.length === 0}
            selected={selected}
            removingId={removingId}
            allPageSelected={allPageSelected}
            selectedOnPage={selectedOnPage}
            onTogglePage={togglePage}
            onOpen={(a) => navigate(`/app/${a.slug}`)}
            onToggleSelect={(a) => toggleRow(a.id)}
            onEdit={editApp}
            onDelete={deleteApp}
            ordem={ordem}
            onOrdenar={ordenarPor}
            filtroCol={filtroCol}
            onFiltrarColuna={filtrarColuna}
            onLimparFiltros={limparFiltrosColuna}
            opcoes={{
              visibilidade: opcoesColuna('visibilidade'),
              situacao: opcoesColuna('situacao'),
              responsavel: opcoesColuna('responsavel'),
              docs: opcoesColuna('docs'),
            }}
            custoDeApp={custoDeApp}
          />

          {/* rodapé · contadores + paginação */}
          <div
            style={{
              ...numeroTabular,
              display: 'flex',
              alignItems: 'center',
              gap: 12,
              flexWrap: 'wrap',
              marginTop: 14,
              fontSize: 11,
              color: p.muted,
            }}
          >
            <span>{rangeSummary(firstShown, lastShown, filtered.length, apps.length)}</span>

            {/* Procedência da coluna de custo: de onde vem, quando foi lida e
                que é ESTIMATIVA. Só aparece quando há dado — sem a rota, a linha
                não existe (e a coluna mostra "—"). */}
            {costs && !costs.semToken && (
              <span
                title="Estimativa do mês corrente: uso medido pelo Railway multiplicado pela tabela de preços. Não é a fatura."
                style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}
              >
                <span style={{ opacity: 0.55 }}>·</span>
                Custo/mês: estimativa do Railway
                {costs.atualizadoEm ? ` · atualizada ${formatDateTimeBR(costs.atualizadoEm)}` : ''}
              </span>
            )}

            {selectedRows.length > 0 && (
              <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
                <span style={{ color: accent, fontWeight: 700 }}>
                  {selectedRows.length} {selectedRows.length === 1 ? 'selecionado' : 'selecionados'}
                </span>
                <button
                  type="button"
                  onClick={() => setSelected(new Set())}
                  style={{
                    background: 'transparent',
                    border: 'none',
                    padding: 0,
                    color: p.muted,
                    fontSize: 11,
                    fontFamily: fontSans,
                    fontWeight: 600,
                    cursor: 'pointer',
                    textDecoration: 'underline',
                    textUnderlineOffset: 3,
                  }}
                >
                  limpar seleção
                </button>
              </span>
            )}

            <div style={{ flex: 1 }} />

            {/* <span>, não <label>: o rótulo acessível já vive no aria-label do
                combobox, e um <label> encaminharia o clique do texto para o
                botão de um jeito que varia entre navegadores. */}
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
              <span>Por página</span>
              <SelectMenu
                value={pageSize}
                onChange={changePageSize}
                label="Por página"
                options={PAGE_SIZES.map((n) => ({ value: n, label: String(n) }))}
                tone={{
                  ...selectTone(dark, accent),
                  // 40px no toque, os 32px originais no desktop.
                  height: byViewport(vp, { mobile: 40, desktop: 32 }),
                  fontSize: 12,
                  radius: 6,
                  fontFamily: numeroTabular.fontFamily,
                }}
              />
            </span>

            {/* A paginação embrulha no celular: "Anterior · 1 2 3 · Próximo" com
                botões de 40px não cabe em 375px ao lado do "Por página". */}
            <div style={{ display: 'flex', gap: 4, flexWrap: 'wrap' }}>
              <GhostButton
                dark={dark}
                disabled={currentPage <= 1}
                onClick={() => patch({ page: currentPage - 1 })}
              >
                Anterior
              </GhostButton>
              {pageWindow(currentPage, totalPages).map(({ key, page: n }) => {
                if (n === null)
                  return (
                    <span
                      key={key}
                      style={{
                        minWidth: 20,
                        // Acompanha a altura do GhostButton para o furo entre
                        // faixas não desalinhar a fileira.
                        height: byViewport(vp, { mobile: 40, desktop: 32 }),
                        display: 'inline-flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      ·
                    </span>
                  );
                return (
                  <GhostButton
                    key={key}
                    dark={dark}
                    numerico
                    active={n === currentPage}
                    title={`Página ${n}`}
                    onClick={() => patch({ page: n })}
                  >
                    {n}
                  </GhostButton>
                );
              })}
              <GhostButton
                dark={dark}
                disabled={currentPage >= totalPages}
                onClick={() => patch({ page: currentPage + 1 })}
              >
                Próximo
              </GhostButton>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
